import { useEffect, useState } from "react";
import type { ProjectResp } from "@pix/shared";
import { ApiError, api } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { makeInitialProject } from "../initialProject";
import { bundleStarterAssets } from "../starterAssets";

interface Props {
  onOpen: (project: ProjectResp) => void;
}

export function ProjectListView({ onOpen }: Props) {
  const { user, logout } = useAuth();
  const [projects, setProjects] = useState<ProjectResp[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [newName, setNewName] = useState("");
  const [progress, setProgress] = useState<string | null>(null);

  const refresh = async () => {
    setErr(null);
    try {
      const ps = await api.listProjects();
      setProjects(ps);
    } catch (e) {
      setErr((e as Error).message);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const createNew = async () => {
    setBusy(true);
    setErr(null);
    try {
      const name = newName.trim() || "新しい作品";
      const starter = makeInitialProject();
      const p = await api.createProject(name, { ...starter.manifest, title: name }, starter.scripts);
      setNewName("");

      // サンプルアセット(SVG)を自動同梱
      setProgress("サンプルアセットを同梱中...");
      try {
        await bundleStarterAssets(p.id, (info) => {
          setProgress(`サンプルアセットを同梱中... (${info.done}/${info.total})`);
        });
      } catch (e) {
        console.warn("starter asset bundling failed:", e);
      }
      setProgress(null);

      onOpen(p);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string, name: string) => {
    if (!confirm(`「${name}」を削除します。よろしいですか?(アセットも含めて消えます)`)) return;
    try {
      await api.deleteProject(id);
      await refresh();
    } catch (e) {
      if (e instanceof ApiError) setErr(e.message);
    }
  };

  return (
    <div className="project-screen">
      <header className="project-header">
        <h1>Pix Studio Online</h1>
        <div className="project-user">
          <span>{user?.displayName}</span>
          <button className="btn btn-tiny btn-secondary" onClick={logout}>
            ログアウト
          </button>
        </div>
      </header>

      <main className="project-main">
        <section className="project-create">
          <h2>新規プロジェクト</h2>
          <div className="project-create-row">
            <input
              type="text"
              placeholder="作品名(例: アリスとの物語)"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void createNew();
              }}
            />
            <button className="btn btn-primary" onClick={() => void createNew()} disabled={busy}>
              作成
            </button>
          </div>
          <small>サンプルシナリオ + 立ち絵/背景 SVG が一式入った状態で作成されます</small>
          {progress && <div className="project-progress">{progress}</div>}
        </section>

        {err && <div className="project-error">{err}</div>}

        <section className="project-list">
          <h2>プロジェクト一覧</h2>
          {projects === null ? (
            <div className="project-loading">読み込み中...</div>
          ) : projects.length === 0 ? (
            <div className="project-empty">プロジェクトはまだありません。上の「新規プロジェクト」から作成してください。</div>
          ) : (
            <ul className="project-grid">
              {projects.map((p) => (
                <li key={p.id} className="project-card">
                  <div className="project-card-body" onClick={() => onOpen(p)}>
                    <div className="project-card-name">{p.name}</div>
                    <div className="project-card-meta">
                      <code>{p.id}</code> · 更新 {formatDate(p.updatedAt)}
                    </div>
                    <div className="project-card-meta">
                      シーン {Object.keys(p.scripts).length} 個
                    </div>
                  </div>
                  <div className="project-card-actions">
                    <button className="btn btn-primary" onClick={() => onOpen(p)}>
                      開く
                    </button>
                    <button
                      className="btn btn-warn btn-tiny"
                      onClick={() => void remove(p.id, p.name)}
                    >
                      削除
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>
    </div>
  );
}

function formatDate(ts: number): string {
  const d = new Date(ts);
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
